import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../api/api-context';
import { useJob } from '../api/useJob';
import type { GameProvider, Job } from '../api/types';

const USERNAME_PATTERN = /^[A-Za-z0-9_-]{2,30}$/;

/** Optional first-session game import. The board remains usable while sync runs. */
export function GameLinkPrompt({ compact = false }: { compact?: boolean }): JSX.Element | null {
  const api = useApi();
  const queryClient = useQueryClient();
  const accounts = useQuery({
    queryKey: ['provider-accounts'],
    queryFn: () => api.listProviderAccounts(),
  });
  const [provider, setProvider] = useState<GameProvider>('chesscom');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = queryClient.getQueryData<{ job: Job; accountId: string }>([
    'active-provider-sync',
  ]);
  const [jobId, setJobId] = useState<string | null>(active?.job.jobId ?? null);
  const [initialJob, setInitialJob] = useState<Job | null>(active?.job ?? null);
  const { ui } = useJob(jobId, initialJob);

  useEffect(() => {
    if (!ui.isTerminal || jobId === null) return;
    void queryClient.invalidateQueries({ queryKey: ['provider-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['explorer'] });
  }, [jobId, queryClient, ui.isTerminal]);

  async function linkAndSync(event: FormEvent): Promise<void> {
    event.preventDefault();
    const name = username.trim();
    if (!USERNAME_PATTERN.test(name) || busy) {
      setError('Enter a chess site username of 2–30 letters, digits, hyphens or underscores.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const linked = await api.linkProviderAccount({ provider, username: name });
      await queryClient.invalidateQueries({ queryKey: ['provider-accounts'] });
      const accepted = await api.syncProviderAccount(
        linked.providerAccountId,
        { maxGames: 250 },
        crypto.randomUUID(),
      );
      queryClient.setQueryData(['active-provider-sync'], {
        job: accepted,
        accountId: linked.providerAccountId,
      });
      setInitialJob(accepted);
      setJobId(accepted.jobId);
      setUsername('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start game import. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const linked = accounts.data ?? [];
  if (compact && linked.length > 0 && (jobId === null || ui.isTerminal)) return null;

  return (
    <section className="game-link-card" aria-labelledby="game-link-title">
      <p className="studio-eyebrow">MAKE IT YOURS</p>
      <h2 id="game-link-title">Use the games you actually play</h2>
      {accounts.isPending ? (
        <p role="status">Checking your chess accounts…</p>
      ) : accounts.isError ? (
        <p role="alert">
          Could not check your chess accounts. <button type="button" onClick={() => void accounts.refetch()}>Try again</button>
        </p>
      ) : linked.length > 0 ? (
        <p>
          Connected: {linked.map((account) => `${account.provider === 'chesscom' ? 'Chess.com' : 'Lichess'} @${account.username}`).join(', ')}.
          {' '}<Link to="/profile#chess-accounts">Manage accounts</Link>
        </p>
      ) : (
        <>
          <p>Connect a Chess.com or Lichess username to import public games and see the replies you face. No chess site password is needed.</p>
          <form onSubmit={(event) => void linkAndSync(event)} aria-label="Connect chess games">
            <label htmlFor={compact ? 'builder-game-site' : 'start-game-site'}>Chess site</label>
            <select
              id={compact ? 'builder-game-site' : 'start-game-site'}
              value={provider}
              disabled={busy}
              onChange={(event) => setProvider(event.target.value as GameProvider)}
            >
              <option value="chesscom">Chess.com</option>
              <option value="lichess">Lichess</option>
            </select>
            <label htmlFor={compact ? 'builder-game-name' : 'start-game-name'}>Username</label>
            <input
              id={compact ? 'builder-game-name' : 'start-game-name'}
              value={username}
              disabled={busy}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="your-username"
            />
            <button type="submit" disabled={busy || username.trim() === ''}>
              {busy ? 'Connecting…' : 'Connect games'}
            </button>
          </form>
          <p className="game-link-footnote">You can start building now; game import runs in the background.</p>
        </>
      )}
      {error && <p role="alert" className="error">{error}</p>}
      {jobId !== null && !ui.isTerminal && <p role="status">{ui.label}{ui.progressLabel ? ` — ${ui.progressLabel}` : ''}</p>}
      {jobId !== null && ui.succeeded && <p role="status">Your imported games are ready to guide your next moves.</p>}
      {jobId !== null && ui.failed && <p role="alert">Game import did not finish. You can try again from Profile.</p>}
    </section>
  );
}
